import React from 'react';
import { getLegalAddendumUrl } from '@shared/navigation';

const landingStyles = `
:root {
  --navy: #05080F;
  --navy-2: #0C1220;
  --blue: #2563EB;
  --blue-light: #3B82F6;
  --emerald: #10B981;
  --white: #ffffff;
  --gray-1: #F9FAFB;
  --gray-3: #E5E7EB;
  --gray-4: #9CA3AF;
  --gray-5: #6B7280;
  --text: #111827;
}

* { margin: 0; padding: 0; box-sizing: border-box; }
html { scroll-behavior: smooth; }

body {
  font-family: 'Inter', -apple-system, sans-serif;
  background: var(--white);
  color: var(--text);
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}

.elfom-nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 200;
  padding: 0 2rem;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(5, 8, 15, 0.88);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(255,255,255,0.06);
}

.elfom-nav-logo {
  font-size: 18px;
  font-weight: 700;
  color: var(--white);
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}

.elfom-nav-logo-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--emerald);
  box-shadow: 0 0 8px rgba(16,185,129,0.6);
  animation: elfom-pulse 2s infinite;
}

@keyframes elfom-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.elfom-nav-links {
  display: flex;
  gap: 2rem;
  list-style: none;
}

.elfom-nav-links a {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255,255,255,0.55);
  text-decoration: none;
  transition: color 0.2s;
}

.elfom-nav-links a:hover { color: var(--white); }

.elfom-nav-contact {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  border: 1px solid rgba(255,255,255,0.15);
  padding: 7px 18px;
  border-radius: 6px;
  transition: all 0.2s;
}

.elfom-nav-contact:hover {
  color: var(--white);
  border-color: rgba(255,255,255,0.35);
  background: rgba(255,255,255,0.06);
}

.elfom-hero {
  min-height: 100vh;
  background: var(--navy);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 8rem 2rem 5rem;
  position: relative;
  overflow: hidden;
}

.elfom-hero-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(37,99,235,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(37,99,235,0.07) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 50%, black 0%, transparent 100%);
}

.elfom-hero-glow {
  position: absolute;
  top: 30%; left: 50%;
  transform: translate(-50%, -50%);
  width: 600px; height: 400px;
  background: radial-gradient(ellipse, rgba(37,99,235,0.2) 0%, rgba(16,185,129,0.08) 40%, transparent 70%);
  pointer-events: none;
}

.elfom-hero-inner {
  position: relative;
  z-index: 1;
  max-width: 900px;
}

.elfom-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(37,99,235,0.12);
  border: 1px solid rgba(37,99,235,0.25);
  border-radius: 100px;
  padding: 6px 16px 6px 8px;
  margin-bottom: 2.5rem;
  font-size: 12px;
  color: rgba(255,255,255,0.8);
  font-weight: 500;
}

.elfom-hero-badge-dot {
  width: 6px; height: 6px;
  background: var(--emerald);
  border-radius: 50%;
}

.elfom-hero h1 {
  font-size: clamp(42px, 7vw, 80px);
  font-weight: 800;
  color: var(--white);
  line-height: 1.05;
  letter-spacing: -2.5px;
  margin-bottom: 1.5rem;
}

.elfom-hero h1 em {
  font-style: normal;
  background: linear-gradient(135deg, var(--blue-light) 0%, var(--emerald) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.elfom-hero-sub {
  font-size: clamp(16px, 2vw, 20px);
  color: rgba(255,255,255,0.5);
  max-width: 560px;
  margin: 0 auto 2.5rem;
  font-weight: 400;
  line-height: 1.65;
}

.elfom-hero-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  padding: 14px 28px;
  margin-bottom: 4rem;
  background: var(--blue);
  color: var(--white);
  box-shadow: 0 0 0 1px var(--blue), 0 4px 20px rgba(37,99,235,0.3);
  transition: all 0.2s;
}

.elfom-hero-cta:hover {
  background: var(--blue-light);
  transform: translateY(-2px);
  box-shadow: 0 0 0 1px var(--blue-light), 0 8px 28px rgba(59,130,246,0.35);
}

.elfom-arch-visual {
  width: 100%;
  max-width: 780px;
  margin: 0 auto;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 16px;
  padding: 2.5rem;
}

.elfom-arch-row {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.elfom-arch-app-pill {
  background: rgba(37,99,235,0.12);
  border: 1px solid rgba(37,99,235,0.2);
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.75);
}

.elfom-arch-arrow {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
  color: rgba(255,255,255,0.2);
  font-size: 20px;
}

.elfom-arch-hub {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, rgba(37,99,235,0.25) 0%, rgba(16,185,129,0.15) 100%);
  border: 1px solid rgba(37,99,235,0.35);
  border-radius: 10px;
  padding: 12px 24px;
  margin-bottom: 1rem;
}

.elfom-arch-hub-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--white);
  letter-spacing: -0.5px;
}

.elfom-arch-hub-status {
  font-size: 11px;
  background: rgba(16,185,129,0.2);
  color: var(--emerald);
  padding: 2px 8px;
  border-radius: 100px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.elfom-arch-pills {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.elfom-arch-pill-sm {
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  padding: 6px 14px;
}

.elfom-stats-section {
  background: var(--gray-1);
  border-top: 1px solid var(--gray-3);
  border-bottom: 1px solid var(--gray-3);
  padding: 3.5rem 2rem;
}

.elfom-stats-grid {
  max-width: 900px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 2rem;
  text-align: center;
}

.elfom-stat-number {
  font-size: 48px;
  font-weight: 800;
  letter-spacing: -2px;
  line-height: 1;
  margin-bottom: 6px;
  background: linear-gradient(135deg, var(--blue) 0%, var(--emerald) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.elfom-stat-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-5);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.elfom-features {
  padding: 7rem 2rem;
  max-width: 1180px;
  margin: 0 auto;
}

.elfom-section-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--blue);
  margin-bottom: 1rem;
}

.elfom-section-heading {
  font-size: clamp(32px, 4vw, 48px);
  font-weight: 800;
  letter-spacing: -1.5px;
  line-height: 1.1;
  margin-bottom: 1rem;
  color: var(--text);
}

.elfom-section-desc {
  font-size: 18px;
  color: var(--gray-5);
  max-width: 520px;
  line-height: 1.6;
  margin-bottom: 4rem;
}

.elfom-features-bento {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.elfom-f-card {
  background: var(--white);
  border: 1px solid var(--gray-3);
  border-radius: 14px;
  padding: 2rem;
  transition: all 0.25s;
  position: relative;
  overflow: hidden;
}

.elfom-f-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--blue) 0%, var(--emerald) 100%);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s;
}

.elfom-f-card:hover {
  border-color: rgba(37,99,235,0.25);
  box-shadow: 0 8px 32px rgba(37,99,235,0.08);
  transform: translateY(-2px);
}

.elfom-f-card:hover::before { transform: scaleX(1); }
.elfom-f-card.large { grid-column: span 2; }

.elfom-f-card-icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.elfom-f-card-icon.blue { background: rgba(37,99,235,0.1); color: var(--blue); }
.elfom-f-card-icon.green { background: rgba(16,185,129,0.1); color: var(--emerald); }

.elfom-f-card h3 {
  font-size: 17px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 0.6rem;
  letter-spacing: -0.3px;
}

.elfom-f-card p {
  font-size: 14px;
  color: var(--gray-5);
  line-height: 1.65;
}

.elfom-apps-section {
  background: var(--navy);
  padding: 7rem 2rem;
  position: relative;
  overflow: hidden;
}

.elfom-apps-section::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(37,99,235,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(37,99,235,0.04) 1px, transparent 1px);
  background-size: 40px 40px;
}

.elfom-apps-inner {
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
}

.elfom-apps-inner .elfom-section-label { color: var(--emerald); }
.elfom-apps-inner .elfom-section-heading { color: var(--white); }
.elfom-apps-inner .elfom-section-desc { color: rgba(255,255,255,0.45); }

.elfom-apps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1px;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  overflow: hidden;
}

.elfom-app-tile {
  background: rgba(255,255,255,0.02);
  padding: 1.75rem;
  transition: background 0.2s;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  border-right: 1px solid rgba(255,255,255,0.04);
  text-decoration: none;
  position: relative;
}

.elfom-app-tile:hover { background: rgba(37,99,235,0.1); }
.elfom-app-tile:hover .elfom-app-tile-arrow { opacity: 1; transform: translate(0, 0); }

.elfom-app-tile-arrow {
  position: absolute;
  top: 1.5rem; right: 1.5rem;
  font-size: 14px;
  color: rgba(255,255,255,0.3);
  opacity: 0;
  transform: translate(-4px, 4px);
  transition: all 0.2s;
}

.elfom-app-tile-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--emerald);
}

.elfom-app-tile-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--white);
  letter-spacing: -0.4px;
}

.elfom-app-tile-desc {
  font-size: 13px;
  color: rgba(255,255,255,0.4);
  line-height: 1.5;
}

.elfom-app-tile-url {
  font-size: 11px;
  color: rgba(255,255,255,0.2);
  margin-top: 0.25rem;
  font-weight: 500;
}

.elfom-app-tile-dev {
  font-size: 10px;
  font-weight: 700;
  color: rgba(255,165,0,0.7);
  background: rgba(255,165,0,0.08);
  border: 1px solid rgba(255,165,0,0.15);
  border-radius: 4px;
  padding: 2px 7px;
  display: inline-block;
  margin-top: 0.25rem;
  letter-spacing: 0.5px;
}

.elfom-cta-section {
  padding: 8rem 2rem;
  text-align: center;
  max-width: 760px;
  margin: 0 auto;
}

.elfom-cta-section .elfom-section-heading { margin-bottom: 1.25rem; }
.elfom-cta-section .elfom-section-desc { margin: 0 auto 2rem; font-size: 17px; }

.elfom-cta-email {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  font-weight: 700;
  color: var(--blue);
  text-decoration: none;
  letter-spacing: -0.5px;
  transition: color 0.2s;
  margin-bottom: 1rem;
}

.elfom-cta-email:hover { color: var(--blue-light); }

.elfom-cta-meta {
  font-size: 13px;
  color: var(--gray-4);
  margin-top: 0.75rem;
}

.elfom-cta-links {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.elfom-cta-links a {
  font-size: 14px;
  color: var(--gray-5);
  text-decoration: none;
  transition: color 0.2s;
}

.elfom-cta-links a:hover { color: var(--text); }

.elfom-footer {
  background: var(--navy-2, #0C1220);
  border-top: 1px solid rgba(255,255,255,0.06);
  padding: 4rem 2rem 2rem;
}

.elfom-footer-top {
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
}

.elfom-footer-brand p {
  font-size: 14px;
  color: rgba(255,255,255,0.3);
  line-height: 1.6;
  margin-top: 0.75rem;
  max-width: 260px;
}

.elfom-footer-logo {
  font-size: 20px;
  font-weight: 700;
  color: var(--white);
  letter-spacing: -0.5px;
}

.elfom-footer-col h5 {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255,255,255,0.25);
  margin-bottom: 1rem;
}

.elfom-footer-col ul { list-style: none; }
.elfom-footer-col li { margin-bottom: 0.6rem; }
.elfom-footer-col a {
  font-size: 14px;
  color: rgba(255,255,255,0.5);
  text-decoration: none;
  transition: color 0.2s;
}
.elfom-footer-col a:hover { color: var(--white); }

.elfom-footer-bottom {
  max-width: 1180px;
  margin: 0 auto;
  padding-top: 2rem;
  border-top: 1px solid rgba(255,255,255,0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.elfom-footer-bottom p {
  font-size: 13px;
  color: rgba(255,255,255,0.2);
}

@media (max-width: 900px) {
  .elfom-features-bento { grid-template-columns: 1fr 1fr; }
  .elfom-f-card.large { grid-column: span 2; }
  .elfom-footer-top { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 600px) {
  .elfom-features-bento { grid-template-columns: 1fr; }
  .elfom-f-card.large { grid-column: span 1; }
  .elfom-footer-top { grid-template-columns: 1fr; }
  .elfom-nav { padding: 0 1.25rem; }
  .elfom-nav-links { display: none; }
  .elfom-hero h1 { letter-spacing: -1.5px; }
}
`;

const Landing: React.FC = () => {
  const privacyUrl = '/privacy';
  const termsUrl = '/terms';
  const addendumUrl = getLegalAddendumUrl();

  return (
    <>
      <style>{landingStyles}</style>

      {/* NAV */}
      <nav className="elfom-nav">
        <a href="#" className="elfom-nav-logo">
          <span className="elfom-nav-logo-dot"></span>
          Elfom Hub
        </a>
        <ul className="elfom-nav-links">
          <li><a href="#features">Plataforma</a></li>
          <li><a href="#apps">Aplicaciones</a></li>
          <li><a href="#contact">Contacto</a></li>
        </ul>
        <a href="mailto:hola@elfom.com" className="elfom-nav-contact">hola@elfom.com</a>
      </nav>

      {/* HERO */}
      <section className="elfom-hero">
        <div className="elfom-hero-grid"></div>
        <div className="elfom-hero-glow"></div>
        <div className="elfom-hero-inner">
          <div className="elfom-hero-badge">
            <span className="elfom-hero-badge-dot"></span>
            Grupo FOM SRL &middot; Santo Domingo, RD &middot; Meta Verified
          </div>
          <h1>Un solo canal.<br /><em>Nueve aplicaciones.</em><br />Sin l&iacute;mites.</h1>
          <p className="elfom-hero-sub">
            Elfom Hub es la infraestructura centralizada de WhatsApp API que conecta todo el ecosistema TheFOM Labs — un punto de entrada &uacute;nico, seguro y verificado por Meta.
          </p>
          <a href="mailto:hola@elfom.com" className="elfom-hero-cta">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
            Contactar al equipo
          </a>

          <div className="elfom-arch-visual">
            <div className="elfom-arch-row">
              <span className="elfom-arch-app-pill">HouseMe</span>
              <span className="elfom-arch-app-pill">La Libreta</span>
              <span className="elfom-arch-app-pill">WashFlow</span>
              <span className="elfom-arch-app-pill">GobScore</span>
              <span className="elfom-arch-app-pill">BankCore</span>
              <span className="elfom-arch-app-pill">+4 m&aacute;s</span>
            </div>
            <div className="elfom-arch-arrow">&darr;</div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="elfom-arch-hub">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" /></svg>
                <span className="elfom-arch-hub-name">Elfom Hub</span>
                <span className="elfom-arch-hub-status">LIVE</span>
              </div>
            </div>
            <div className="elfom-arch-arrow">&darr;</div>
            <div className="elfom-arch-pills">
              <span className="elfom-arch-pill-sm" style={{ color: 'rgba(16,185,129,0.8)', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)' }}>WhatsApp Business API</span>
              <span className="elfom-arch-pill-sm" style={{ color: 'rgba(37,99,235,0.8)', background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.15)' }}>Meta Verified</span>
              <span className="elfom-arch-pill-sm" style={{ color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>elfom.com</span>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="elfom-stats-section">
        <div className="elfom-stats-grid">
          <div>
            <div className="elfom-stat-number">9</div>
            <div className="elfom-stat-label">Aplicaciones activas</div>
          </div>
          <div>
            <div className="elfom-stat-number">1</div>
            <div className="elfom-stat-label">Canal unificado</div>
          </div>
          <div>
            <div className="elfom-stat-number">17+</div>
            <div className="elfom-stat-label">A&ntilde;os de operaci&oacute;n</div>
          </div>
          <div>
            <div className="elfom-stat-number">&infin;</div>
            <div className="elfom-stat-label">Escalabilidad</div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="elfom-features" id="features">
        <div className="elfom-section-label">Plataforma</div>
        <h2 className="elfom-section-heading">Infraestructura hecha<br />para escalar.</h2>
        <p className="elfom-section-desc">Un punto de entrada, control total. Dise&ntilde;ado para el Caribe, construido para el mundo.</p>

        <div className="elfom-features-bento">
          <div className="elfom-f-card large">
            <div className="elfom-f-card-icon blue">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 20V10M12 20V4M6 20v-6" /></svg>
            </div>
            <h3>Un canal, todas las apps</h3>
            <p>Sin importar de qu&eacute; aplicaci&oacute;n provenga el mensaje — HouseMe, La Libreta, WashFlow — todos viajan por el mismo canal verificado con Meta. Cero duplicaci&oacute;n, cero overhead operacional.</p>
          </div>
          <div className="elfom-f-card">
            <div className="elfom-f-card-icon green">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            </div>
            <h3>Enterprise security</h3>
            <p>JWT, RLS policies y cifrado de extremo a extremo en cada mensaje.</p>
          </div>
          <div className="elfom-f-card">
            <div className="elfom-f-card-icon blue">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" /></svg>
            </div>
            <h3>Routing inteligente</h3>
            <p>Detecta el origen de cada mensaje y lo enruta con contexto autom&aacute;tico.</p>
          </div>
          <div className="elfom-f-card">
            <div className="elfom-f-card-icon green">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
            </div>
            <h3>Observabilidad total</h3>
            <p>Logs, m&eacute;tricas y analytics en tiempo real de cada interacci&oacute;n.</p>
          </div>
          <div className="elfom-f-card">
            <div className="elfom-f-card-icon blue">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
            </div>
            <h3>Deploy sin fricci&oacute;n</h3>
            <p>Integra una nueva app sin reconfigurar Meta. El hub la detecta autom&aacute;ticamente.</p>
          </div>
          <div className="elfom-f-card">
            <div className="elfom-f-card-icon green">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            </div>
            <h3>Soporte 24/7</h3>
            <p>Equipo t&eacute;cnico en Santo Domingo. Respuesta r&aacute;pida, siempre disponible.</p>
          </div>
        </div>
      </section>

      {/* APPS */}
      <section className="elfom-apps-section" id="apps">
        <div className="elfom-apps-inner">
          <div className="elfom-section-label">Ecosistema TheFOM Labs</div>
          <h2 className="elfom-section-heading">Nueve productos.<br />Un ecosistema.</h2>
          <p className="elfom-section-desc">Cada app conectada a Elfom Hub. Haz clic para visitar.</p>

          <div className="elfom-apps-grid">
            <a href="https://houseme.app" target="_blank" rel="noopener noreferrer" className="elfom-app-tile">
              <span className="elfom-app-tile-arrow">&nearr;</span>
              <div className="elfom-app-tile-tag">Real Estate &middot; Fintech</div>
              <div className="elfom-app-tile-name">HouseMe</div>
              <div className="elfom-app-tile-desc">Plataforma de real estate con marketplace, fintech e insurtech</div>
              <div className="elfom-app-tile-url">houseme.app</div>
            </a>
            <a href="https://lalibreta.app" target="_blank" rel="noopener noreferrer" className="elfom-app-tile">
              <span className="elfom-app-tile-arrow">&nearr;</span>
              <div className="elfom-app-tile-tag">Cr&eacute;dito informal</div>
              <div className="elfom-app-tile-name">La Libreta</div>
              <div className="elfom-app-tile-desc">El f&iacute;o digitalizado — cr&eacute;dito informal para el mercado caribe&ntilde;o</div>
              <div className="elfom-app-tile-url">lalibreta.app</div>
            </a>
            <a href="https://gobscore.app" target="_blank" rel="noopener noreferrer" className="elfom-app-tile">
              <span className="elfom-app-tile-arrow">&nearr;</span>
              <div className="elfom-app-tile-tag">Scoring &middot; ML</div>
              <div className="elfom-app-tile-name">GobScore</div>
              <div className="elfom-app-tile-desc">Evaluaci&oacute;n crediticia con machine learning para el Caribe</div>
              <div className="elfom-app-tile-url">gobscore.app</div>
            </a>
            <a href="https://theplatform360.app" target="_blank" rel="noopener noreferrer" className="elfom-app-tile">
              <span className="elfom-app-tile-arrow">&nearr;</span>
              <div className="elfom-app-tile-tag">ERP &middot; IA Agente</div>
              <div className="elfom-app-tile-name">ThePlatform360</div>
              <div className="elfom-app-tile-desc">ERP empresarial con HAL-TR, agente IA para operaciones</div>
              <div className="elfom-app-tile-url">theplatform360.app</div>
            </a>
            <a href="https://aroly.app" target="_blank" rel="noopener noreferrer" className="elfom-app-tile">
              <span className="elfom-app-tile-arrow">&nearr;</span>
              <div className="elfom-app-tile-tag">Especializada</div>
              <div className="elfom-app-tile-name">Aroly</div>
              <div className="elfom-app-tile-desc">Plataforma vertical en desarrollo activo</div>
              <div className="elfom-app-tile-url">aroly.app</div>
              <span className="elfom-app-tile-dev">EN DESARROLLO</span>
            </a>
            <a href="https://isela.app" target="_blank" rel="noopener noreferrer" className="elfom-app-tile">
              <span className="elfom-app-tile-arrow">&nearr;</span>
              <div className="elfom-app-tile-tag">Especializada</div>
              <div className="elfom-app-tile-name">Isela</div>
              <div className="elfom-app-tile-desc">Soluci&oacute;n de nicho en evoluci&oacute;n continua</div>
              <div className="elfom-app-tile-url">isela.app</div>
              <span className="elfom-app-tile-dev">EN DESARROLLO</span>
            </a>
            <a href="https://pawtys.app" target="_blank" rel="noopener noreferrer" className="elfom-app-tile">
              <span className="elfom-app-tile-arrow">&nearr;</span>
              <div className="elfom-app-tile-tag">Especializada</div>
              <div className="elfom-app-tile-name">Pawtys</div>
              <div className="elfom-app-tile-desc">Aplicaci&oacute;n especializada del ecosistema</div>
              <div className="elfom-app-tile-url">pawtys.app</div>
              <span className="elfom-app-tile-dev">EN DESARROLLO</span>
            </a>
            <a href="https://sonmany.app" target="_blank" rel="noopener noreferrer" className="elfom-app-tile">
              <span className="elfom-app-tile-arrow">&nearr;</span>
              <div className="elfom-app-tile-tag">M&uacute;ltiple</div>
              <div className="elfom-app-tile-name">SonMany</div>
              <div className="elfom-app-tile-desc">Suite de aplicaciones especializadas del portafolio</div>
              <div className="elfom-app-tile-url">sonmany.app</div>
              <span className="elfom-app-tile-dev">EN DESARROLLO</span>
            </a>
            <a href="https://thefomlabs.com" target="_blank" rel="noopener noreferrer" className="elfom-app-tile">
              <span className="elfom-app-tile-arrow">&nearr;</span>
              <div className="elfom-app-tile-tag">Infraestructura &middot; Core</div>
              <div className="elfom-app-tile-name">TheFOM Labs</div>
              <div className="elfom-app-tile-desc">Portfolio completo y centro de operaciones del ecosistema</div>
              <div className="elfom-app-tile-url">thefomlabs.com</div>
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="elfom-cta-section" id="contact">
        <div className="elfom-section-label">Contacto</div>
        <h2 className="elfom-section-heading">&iquest;Preguntas sobre<br />la infraestructura?</h2>
        <p className="elfom-section-desc">Elfom Hub es infraestructura interna del ecosistema TheFOM Labs. Para integraciones o consultas t&eacute;cnicas, escr&iacute;benos directamente.</p>
        <a href="mailto:hola@elfom.com" className="elfom-cta-email">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
          hola@elfom.com
        </a>
        <p className="elfom-cta-meta">Grupo FOM SRL &middot; Santo Domingo, Rep&uacute;blica Dominicana</p>
        <div className="elfom-cta-links">
          <a href={privacyUrl}>Pol&iacute;tica de Privacidad</a>
          <a href={termsUrl}>T&eacute;rminos de Servicio</a>
          <a href={addendumUrl} target="_blank" rel="noopener noreferrer">Addendum Legal</a>
          <a href="https://thefomlabs.com" target="_blank" rel="noopener noreferrer">TheFOM Labs</a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="elfom-footer">
        <div className="elfom-footer-top">
          <div className="elfom-footer-brand">
            <div className="elfom-footer-logo">Elfom Hub</div>
            <p>Infraestructura centralizada de WhatsApp API para el ecosistema TheFOM Labs. Por Grupo FOM SRL, Santo Domingo, Rep&uacute;blica Dominicana.</p>
          </div>
          <div className="elfom-footer-col">
            <h5>Ecosistema</h5>
            <ul>
              <li><a href="https://houseme.app" target="_blank" rel="noopener noreferrer">HouseMe</a></li>
              <li><a href="https://lalibreta.app" target="_blank" rel="noopener noreferrer">La Libreta</a></li>
              <li><a href="https://gobscore.app" target="_blank" rel="noopener noreferrer">GobScore</a></li>
              <li><a href="https://theplatform360.app" target="_blank" rel="noopener noreferrer">ThePlatform360</a></li>
              <li><a href="https://thefomlabs.com" target="_blank" rel="noopener noreferrer">TheFOM Labs</a></li>
            </ul>
          </div>
          <div className="elfom-footer-col">
            <h5>En desarrollo</h5>
            <ul>
              <li><a href="https://aroly.app" target="_blank" rel="noopener noreferrer">Aroly</a></li>
              <li><a href="https://isela.app" target="_blank" rel="noopener noreferrer">Isela</a></li>
              <li><a href="https://pawtys.app" target="_blank" rel="noopener noreferrer">Pawtys</a></li>
              <li><a href="https://sonmany.app" target="_blank" rel="noopener noreferrer">SonMany</a></li>
            </ul>
          </div>
          <div className="elfom-footer-col">
            <h5>Legal</h5>
            <ul>
              <li><a href={privacyUrl}>Privacidad</a></li>
              <li><a href={termsUrl}>T&eacute;rminos</a></li>
              <li><a href={addendumUrl} target="_blank" rel="noopener noreferrer">Addendum Legal</a></li>
              <li><a href="mailto:hola@elfom.com">Contacto</a></li>
            </ul>
          </div>
        </div>
        <div className="elfom-footer-bottom">
          <p>&copy; 2026 Grupo FOM SRL &middot; RNC 133308355. Todos los derechos reservados.</p>
          <p>elfom.com &middot; thefomlabs.com</p>
        </div>
      </footer>
    </>
  );
};

export default Landing;
